/**
 * Users — companies first, then that company's people.
 *
 * ─── WHAT CHANGED HERE, AND WHY EACH ONE WAS NOT COSMETIC ────────────────
 *
 * 1. EVERY COLOUR IS A TOKEN NOW. This file drew 43 lines of Tailwind's own
 *    palette — `text-slate-900`, `bg-slate-50`, `bg-blue-50`, `text-slate-400`
 *    — none of which move with the theme and none of which the contrast suite
 *    can see, because that suite measures TOKENS out of `index.css` and a
 *    compiled literal is not one. Measured in a real browser over the compiled
 *    CSS, in dark, this page had 32 failing strings out of 54, worst 1.02:1:
 *
 *        every user's own name      text-slate-900   1.02:1   (invisible)
 *        the "Platform Users" h1    text-slate-900   1.08:1
 *        every email address        text-slate-600   2.40:1
 *        every table header         text-muted-foreground on bg-slate-50
 *                                                    2.45:1
 *
 *    That last one is the pair worth remembering: a TOKEN foreground over a
 *    LITERAL background. The token suite saw a legal ink and passed; the
 *    literal ban would have caught the ground. Neither half alone finds it.
 *
 * 2. THE STATUS BADGE WAS LYING. `config[status] || config.active` — three
 *    known keys and a fallback that drew every other value, including
 *    `suspended`, as a green tick reading "Active". See `userPresentation.js`.
 *
 * 3. AN ERROR LOOKED LIKE AN EMPTY ESTATE. The company fetch caught its own
 *    failure, set the list to `[]`, and rendered "No companies yet." An
 *    operator whose request had just failed was told the platform has no
 *    customers. Unavailable is not none.
 *
 * 4. THE AVATAR GRADIENT WAS THE ONE ALREADY DELETED ELSEWHERE.
 *    `bg-gradient-to-br from-indigo-500 to-purple-600` is not on the Cluvik
 *    ramp — it is Tailwind's indigo and purple — and it is character for
 *    character the tile that `TenantMark.jsx` was written to replace on the
 *    tenants list. It survived here because nobody re-looked at this page.
 *
 *    IT IS NOT REPLACED BY A COLOURED TILE. A person is not one of the eight
 *    product areas, and the tenant mark's own notes record why forty
 *    dimensional tiles down a table are wrong: they say nothing, and they make
 *    the one mark on the page that IS an area identity compete with forty
 *    imitations of itself. The user avatar is flat and monochrome. The COMPANY
 *    list above it does get `TenantMark`, because a company is a place you
 *    navigate to and the tenants list already marks it that way.
 *
 * 5. "LOGIN AS USER" — see the block above `LoginAsUser` below. It is the
 *    consequential action on this screen and it was an unlabelled 32px icon.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../auth/AdminAuthContext';
import { PermissionGate, usePermission } from '../components';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import StatusBadge from '../../../components/admin/StatusBadge';
import TenantMark from '../../../design-system/identity/TenantMark';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../../components/ui/table';
import {
  Users,
  Search,
  Loader2,
  AlertTriangle,
  UserCog,
  Shield,
  RefreshCw,
  ExternalLink,
  LogIn,
  Mail,
  ChevronRight,
  ChevronLeft
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import ImpersonationReasonDialog from '../tenants/ImpersonationReasonDialog';
import {
  describeUserStatus,
  describeUserSetup,
  describeLoginAs,
  describeList,
  ESTATE,
  userInitials,
  userDisplayName,
} from './userPresentation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_API = `${BACKEND_URL}/api/admin`;
const CONTROL_PLANE_API = `${BACKEND_URL}/api/admin/control-plane`;

/**
 * A person's avatar. FLAT, MONOCHROME, and deliberately not identity artwork.
 *
 * `bg-muted` / `text-muted-foreground` are the two tokens that carry a quiet
 * pair in both themes, so this is one shape that does not need a theme branch.
 */
const UserAvatar = ({ user }) => (
  <span
    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted ring-1 ring-inset ring-border"
    aria-hidden="true"
  >
    <span className="text-sm font-semibold text-muted-foreground">
      {userInitials(user.first_name, user.last_name, user.email)}
    </span>
  </span>
);

/**
 * ─── "LOGIN AS USER" ─────────────────────────────────────────────────────
 *
 * WHAT IT WAS. A 32×32 ghost button carrying a `LogIn` glyph and a `title`
 * attribute. No visible label, no confirmation, no accessible name beyond the
 * tooltip, and — measured in the browser — a 20px hit target on the row. The
 * single most consequential control in the Admin portal, drawn smaller and
 * quieter than the Refresh button above it.
 *
 * WHY THAT IS A DESIGN DEFECT AND NOT A TASTE ONE. Activating it makes the
 * operator *be* a named human being inside that customer's live data. Every
 * other irreversible action in this portal — suspend, delete, open workspace —
 * states what it is about to do and asks. This one did not, and the P0 note on
 * this feature records exactly the failure that produces: an operator who lands
 * in a working CRM cannot tell from the screen whose CRM it is.
 *
 * WHAT IT IS NOW.
 *
 *   FLAT AND MONOCHROME, DELIBERATELY. Direction A reserves dimensional,
 *   coloured artwork for identity — for places. Signing in as somebody is an
 *   ACTION, and the rule is explicit that turning an action into identity
 *   artwork tells an operator the control is a place. So: an outline button,
 *   one stroke weight, `text-foreground` ink, no ramp colour anywhere on it.
 *   Its weight comes from being LABELLED and full-size, not from being bright.
 *
 *   IT SAYS WHO. The label is "Login as"; the accessible name and the
 *   confirmation both name the person and the company, because "as this user"
 *   is only unambiguous while your eye is on the row.
 *
 *   IT ASKS FIRST — FOR A REASON, NOT A YES. The click opens the shared
 *   `ImpersonationReasonDialog`, which names the person and the company and
 *   refuses to proceed without the operator's own reason (the backend records
 *   it on both sides of the repo boundary). The confirm there is the ordinary
 *   primary rather than the destructive tone — impersonation is consequential,
 *   not destructive, and spending the destructive tone on it would devalue it
 *   where it is earned.
 *
 *   IT EXPLAINS ITS REFUSALS. Disabled used to mean any of three unrelated
 *   things. Each now carries its own sentence from `describeLoginAs`.
 *
 * ONE MECHANISM. The mint and the redirect live in `startViewAs`, behind the
 * dialog — the shared authority that `TenantDetailPage`, the tenant overview
 * and User Detail already use. This page was the last place still writing the
 * mint-and-redirect out longhand, and its copy had drifted in two ways that
 * matter: it opened the tab AFTER awaiting the mint, which Chrome blocks as an
 * unrequested popup, and it sent the browser to `{CRM}/impersonate/{token}` —
 * the PATH form, which puts one-time credential material in the request line
 * and which `startViewAs` documents as the form Admin must not mint any more
 * of. This file never builds a CRM URL and carries no reason literal.
 */
const LoginAsUser = ({ user, tenantName, onConfirm }) => {
  const { allowed } = usePermission('tenant:impersonate');
  const availability = describeLoginAs({ status: user.status, permitted: allowed });
  const who = userDisplayName(user);

  return (
    <PermissionGate permission="tenant:impersonate" tooltip={false}>
      <Button
        variant="outline"
        size="sm"
        className="h-8 gap-1.5 px-2.5 text-xs font-medium"
        disabled={!availability.enabled}
        title={availability.reason || `Open the CRM as ${who}`}
        aria-label={`Login as ${who} at ${tenantName || 'this company'}`}
        onClick={() => onConfirm(user)}
        data-testid={`impersonate-user-${user.id}`}
      >
        <LogIn className="h-3.5 w-3.5" aria-hidden="true" />
        Login as
      </Button>
    </PermissionGate>
  );
};

const UsersPage = () => {
  const { getAdminToken } = useAdminAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  // Who the operator is about to sign in as. Non-null opens the reason dialog;
  // the mint and redirect live in startViewAs, behind that dialog.
  const [viewAsTarget, setViewAsTarget] = useState(null);
  // UNAVAILABLE IS NOT NONE. Two lists, two failure flags, because an empty
  // array is the answer to a different question than "did it answer".
  const [tenantsFailed, setTenantsFailed] = useState(false);
  const [usersFailed, setUsersFailed] = useState(false);
  const limit = 25;

  // TENANT-FIRST. The page used to render every user of every tenant as one
  // flat fleet-wide table — 627 rows across 331 companies — which is not a
  // view an operator can act on. Users only mean anything inside the company
  // they belong to, and every action on them (impersonate, reset password,
  // check a licence) is tenant-scoped anyway. So: companies, then that
  // company's users. `selectedTenant === null` is the company list.
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [tenantSearch, setTenantSearch] = useState('');
  const [tenantTotal, setTenantTotal] = useState(0);
  const [tenantPage, setTenantPage] = useState(0);
  const [licenseFilter, setLicenseFilter] = useState('all');
  const [licenses, setLicenses] = useState([]);

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${ADMIN_API}/users/by-tenant`, {
        params: { skip: tenantPage * limit, limit, search: tenantSearch || undefined },
        headers: { Authorization: `Bearer ${getAdminToken()}` },
      });
      setTenants(data.tenants || []);
      setTenantTotal(data.total || 0);
      setTenantsFailed(false);
    } catch (err) {
      console.error('Failed to load companies:', err);
      toast.error('Could not load companies');
      setTenants([]);
      // ...and RECORD that it failed. Without this the render below cannot
      // tell "no companies" from "we do not know".
      setTenantsFailed(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedTenant) {
      const id = setTimeout(fetchTenants, 300);
      return () => clearTimeout(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenant, tenantSearch, tenantPage]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${ADMIN_API}/license-catalog`, {
          headers: { Authorization: `Bearer ${getAdminToken()}` },
        });
        setLicenses(data.licenses || data || []);
      } catch { setLicenses([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Login as this user. The reason is the operator's own, asked for in the
  // dialog; the grant is minted and the tab redirected by startViewAs, the one
  // authority for the URL form. This page never builds a CRM URL.
  const handleImpersonateUser = (user) => {
    setViewAsTarget({
      tenantId: user.tenant_id,
      tenantName: user.tenant_name,
      userId: user.id,
      userEmail: user.email,
      userName: [user.first_name, user.last_name].filter(Boolean).join(' ') || undefined,
    });
  };

  const postSupportAction = (path, body) => axios.post(
    `${CONTROL_PLANE_API}${path}`, body,
    { headers: { Authorization: `Bearer ${getAdminToken()}` } },
  );

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {
        skip: page * limit,
        limit,
        search: searchQuery || undefined,
        role: roleFilter !== 'all' ? roleFilter : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        license_code: licenseFilter !== 'all' ? licenseFilter : undefined,
        // ALWAYS tenant-scoped. Without this the query is fleet-wide, which is
        // the shape this page is moving away from.
        tenant_id: selectedTenant?.id,
      };

      const response = await axios.get(`${ADMIN_API}/users`, {
        headers: { Authorization: `Bearer ${getAdminToken()}` },
        params
      });
      setUsers(response.data.users || []);
      setTotal(response.data.total || 0);
      setUsersFailed(false);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setUsers([]);
      setUsersFailed(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTenant) fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when a filter/page value changes
  }, [selectedTenant, page, roleFilter, statusFilter, licenseFilter]);

  useEffect(() => {
    if (!selectedTenant) return undefined;
    const delaySearch = setTimeout(() => {
      setPage(0);
      fetchUsers();
    }, 300);
    return () => clearTimeout(delaySearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when the search query changes
  }, [searchQuery, selectedTenant]);

  /**
   * Capability first, label second.
   *
   * `role` is this portal's own descriptive label; it does not decide what the
   * user can do. `is_system_administrator` does, and it is derived from the
   * three paths the platform actually grants on. Rendering the label alone —
   * and falling back to the "user" styling whenever it was absent — displayed
   * every tenant's System Administrator as a plain user, because a correctly
   * provisioned admin carries no hierarchy role at all.
   *
   * The four tinted variants this used to draw were four raw palettes
   * (purple-100/amber-100/blue-100/slate-100) that did not move with the theme.
   * A role is a LABEL, not a health state, so it now draws in one quiet outline
   * and the icon carries the distinction — which is the same argument the
   * identity system makes about silhouette over colour.
   */
  const getRoleBadge = (user) => {
    const ICONS = { admin: Shield, owner: Shield, manager: UserCog, user: Users };
    const Icon = ICONS[user.role];
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {user.is_system_administrator && (
          <Badge variant="brand" className="flex items-center gap-1">
            <Shield className="h-3 w-3" aria-hidden="true" />
            System Administrator
          </Badge>
        )}
        {Icon ? (
          <Badge variant="outline" className="flex items-center gap-1 font-normal text-foreground">
            <Icon className="h-3 w-3" aria-hidden="true" />
            <span className="capitalize">{user.role}</span>
          </Badge>
        ) : (
          !user.is_system_administrator && (
            <span className="text-xs text-muted-foreground">No label</span>
          )
        )}
      </div>
    );
  };

  const totalPages = Math.ceil(total / limit);

  // ---- COMPANY LIST -------------------------------------------------------
  if (!selectedTenant) {
    const state = describeList({
      failed: tenantsFailed, rows: tenants, filtered: Boolean(tenantSearch),
    });
    return (
      <div className="p-6 max-w-[1600px] mx-auto" data-testid="users-company-list">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Users</h1>
          <p className="text-muted-foreground mt-1">
            Choose a company to manage its users. Every action — sign in as someone, reset a
            password, check a licence — is scoped to that company.
          </p>
        </div>

        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <Input
                placeholder="Search companies…"
                className="pl-9"
                value={tenantSearch}
                onChange={(e) => { setTenantPage(0); setTenantSearch(e.target.value); }}
                data-testid="company-search"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Companies ({tenantTotal})</CardTitle>
            <CardDescription>User counts are live per company.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
                <span className="sr-only">Loading companies</span>
              </div>
            ) : state === ESTATE.UNAVAILABLE ? (
              /* NOT "no companies yet". The list did not load; saying the
                 platform has no customers would be a fact this screen does not
                 have. The retry is here because it is the only useful action. */
              <div className="px-6 py-12 text-center" data-testid="companies-unavailable">
                <AlertTriangle className="mx-auto mb-3 h-8 w-8 text-warning-text" aria-hidden="true" />
                <p className="text-sm font-semibold text-foreground">
                  The company list could not be loaded
                </p>
                <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                  This is not the same as there being no companies — nothing was read,
                  so nothing is known.
                </p>
                <Button variant="outline" size="sm" className="mt-4" onClick={fetchTenants}>
                  <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" /> Try again
                </Button>
              </div>
            ) : state === ESTATE.FILTERED_OUT ? (
              <p className="py-12 text-center text-sm text-muted-foreground">
                No companies match “{tenantSearch}”.
              </p>
            ) : state === ESTATE.EMPTY ? (
              <p className="py-12 text-center text-sm text-muted-foreground">
                No companies yet.
              </p>
            ) : (
              <div className="divide-y divide-border">
                {tenants.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => { setSelectedTenant(t); setPage(0); setSearchQuery(''); }}
                    className="w-full text-left px-5 py-3 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset flex items-center justify-between gap-4"
                    data-testid={`company-${t.id}`}
                  >
                    <span className="flex min-w-0 items-center gap-3">
                      {/* A company IS a place you go to, so it carries the
                          tenant mark — the same artwork the tenants list uses,
                          so the same customer looks like the same thing on
                          every screen. */}
                      <TenantMark
                        tenantId={t.id}
                        name={t.company_name || t.tenant_name}
                        size="md"
                        className="shrink-0"
                      />
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-foreground">
                          {t.company_name || t.tenant_name}
                        </span>
                        {t.subdomain ? (
                          <span className="block truncate font-mono text-xs text-muted-foreground">
                            {t.subdomain}
                          </span>
                        ) : null}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-3 text-sm text-muted-foreground">
                      <span>{t.user_count} user{t.user_count === 1 ? '' : 's'}</span>
                      {t.disabled_user_count > 0 && (
                        <Badge variant="outline" className="text-xs text-foreground">
                          {t.disabled_user_count} disabled
                        </Badge>
                      )}
                      <ChevronRight className="h-4 w-4" aria-hidden="true" />
                    </span>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {tenantTotal > limit && (
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-muted-foreground">
              {tenantPage * limit + 1}-{Math.min((tenantPage + 1) * limit, tenantTotal)} of {tenantTotal}
            </span>
            <span className="flex gap-2">
              <Button variant="outline" size="sm" disabled={tenantPage === 0}
                      onClick={() => setTenantPage((p) => p - 1)}>Previous</Button>
              <Button variant="outline" size="sm"
                      disabled={(tenantPage + 1) * limit >= tenantTotal}
                      onClick={() => setTenantPage((p) => p + 1)}>Next</Button>
            </span>
          </div>
        )}
      </div>
    );
  }

  // ---- USERS WITHIN ONE COMPANY -------------------------------------------
  const filtered = Boolean(searchQuery) || roleFilter !== 'all'
    || statusFilter !== 'all' || licenseFilter !== 'all';
  const listState = describeList({ failed: usersFailed, rows: users, filtered });
  const companyName = selectedTenant.company_name || selectedTenant.tenant_name;

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <Button variant="ghost" size="sm" className="mb-3 -ml-2"
              onClick={() => { setSelectedTenant(null); setLicenseFilter('all'); }}
              data-testid="back-to-companies">
        <ChevronLeft className="h-4 w-4 mr-1" aria-hidden="true" /> All companies
      </Button>

      {/* Header. The company is the subject of this screen — every row below it
          belongs to this one customer — so it is named in the h1 and carries
          the same mark the company list drew, rather than the page repeating
          the generic word "Platform Users" that told an operator nothing about
          which estate they were looking at. */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <TenantMark
            tenantId={selectedTenant.id}
            name={companyName}
            size="lg"
            className="mt-0.5 shrink-0"
          />
          <div className="min-w-0">
            <h1 className="truncate text-2xl font-bold tracking-tight text-foreground">
              {companyName}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Users in this company. Creating and editing users happens on the
              company&apos;s own Users tab.
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchUsers}>
          <RefreshCw className="h-4 w-4 mr-2" aria-hidden="true" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[250px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                aria-label="Search users by name or email"
              />
            </div>

            <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[130px]" aria-label="Filter by role">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="user">User</SelectItem>
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[130px]" aria-label="Filter by status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="disabled">Disabled</SelectItem>
                <SelectItem value="invited">Invited</SelectItem>
              </SelectContent>
            </Select>

            {/* Licence holders. Rendered only when the catalogue has entries —
                an empty dropdown implies a filter that does nothing. */}
            {licenses.length > 0 && (
              <Select value={licenseFilter} onValueChange={(v) => { setLicenseFilter(v); setPage(0); }}>
                <SelectTrigger className="w-[170px]" data-testid="license-filter" aria-label="Filter by licence">
                  <SelectValue placeholder="Licence" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All licences</SelectItem>
                  {licenses.map((l) => (
                    <SelectItem key={l.license_code} value={l.license_code}>
                      {l.license_name || l.license_code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {filtered && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setRoleFilter('all');
                  setStatusFilter('all');
                  setLicenseFilter('all');
                  setSearchQuery('');
                  setPage(0);
                }}
                className="text-muted-foreground"
              >
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader className="pb-3 border-b border-border">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            Users ({total})
          </CardTitle>
          <CardDescription>
            Signing in as someone opens their live CRM session and is recorded.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden="true" />
              <span className="sr-only">Loading users</span>
            </div>
          ) : listState === ESTATE.UNAVAILABLE ? (
            <div className="px-6 py-16 text-center" data-testid="users-unavailable">
              <AlertTriangle className="mx-auto mb-3 h-10 w-10 text-warning-text" aria-hidden="true" />
              <p className="text-lg font-medium text-foreground">
                The user list could not be loaded
              </p>
              <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                Nothing was read for {companyName}, so this screen cannot say whether
                it has users or not.
              </p>
              <Button variant="outline" size="sm" className="mt-4" onClick={fetchUsers}>
                <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" /> Try again
              </Button>
            </div>
          ) : listState !== ESTATE.LOADED ? (
            <div className="text-center py-16">
              {/* `text-input`, not a divided `--muted-foreground`. The muted
                  token is tuned to just clear 4.5:1 and dividing it is
                  arithmetically guaranteed to fail — `tokenRoles.test.js`
                  bans the shape and names `--input`, which carries a 3:1
                  floor, as where a deliberately quieter GRAPHIC goes. It
                  caught this line the first time it was written. */}
              <Users className="h-12 w-12 mx-auto mb-3 text-input" aria-hidden="true" />
              <p className="text-lg font-medium text-foreground">No users found</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {listState === ESTATE.FILTERED_OUT
                  ? 'No user in this company matches the current filters.'
                  : `${companyName} has no users yet.`}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  {/* `bg-muted/50`, not `bg-slate-50`. The literal was a LIGHT
                      ground that stayed light in dark mode while the token ink
                      on top of it flipped, so every column heading measured
                      2.45:1 — a token foreground defeated by a literal
                      background, which neither half of the suite finds alone. */}
                  <TableRow className="bg-muted/50">
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Subdomain</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => {
                    const described = describeUserStatus(user.status);
                    return (
                      <TableRow key={user.id} className="hover:bg-muted/40 transition-colors">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <UserAvatar user={user} />
                            <div className="min-w-0">
                              <Button
                                variant="link"
                                className="h-auto p-0 font-medium text-foreground hover:text-primary truncate"
                                onClick={() => navigate(`/users/${user.id}`)}
                                data-testid={`user-link-${user.id}`}
                              >
                                {userDisplayName(user)}
                              </Button>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-foreground">
                            <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                            <span className="text-sm truncate max-w-[220px]">{user.email}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground font-mono">
                            {user.tenant_subdomain || '—'}
                          </span>
                        </TableCell>
                        <TableCell>
                          {getRoleBadge(user)}
                        </TableCell>
                        <TableCell>
                          {/* Through the shared vocabulary, and truthfully: an
                              unrecognised status renders as itself in the muted
                              tone. It never borrows the green tick. */}
                          <StatusBadge
                            status={described.tone}
                            label={described.label}
                            className={described.known ? undefined : 'italic'}
                          />
                          {describeUserSetup(user) && (
                            <span className="block mt-1 text-xs text-muted-foreground">
                              {describeUserSetup(user)}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {user.last_login
                            ? new Date(user.last_login).toLocaleDateString()
                            : 'Never'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {user.created_at
                            ? new Date(user.created_at).toLocaleDateString()
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-2">
                            <LoginAsUser
                              user={user}
                              tenantName={companyName}
                              onConfirm={handleImpersonateUser}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => navigate(`/users/${user.id}`)}
                              title="Open user"
                              aria-label={`Open the record for ${userDisplayName(user)}`}
                              data-testid={`open-user-${user.id}`}
                            >
                              <ExternalLink className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/50">
              <p className="text-sm text-muted-foreground">
                Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total} users
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  Previous
                </Button>
                <span className="text-sm text-foreground px-2">
                  Page {page + 1} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <ImpersonationReasonDialog
        target={viewAsTarget}
        post={postSupportAction}
        onClose={() => setViewAsTarget(null)}
      />

      {/* Where the other half of user management lives. `bg-info/10` +
          `text-info-text`, not `bg-blue-50` + `text-blue-800`: the literal pair
          was a light panel that stayed light in dark mode. */}
      <div className="mt-4 rounded-xl border border-info/25 bg-info/10 px-4 py-3">
        <p className="text-sm text-foreground">
          <strong className="font-semibold">Note:</strong> this screen monitors users and
          opens sessions as them. To create, edit or disable a user, open the company&apos;s
          tenant record and use its <strong className="font-semibold">Users tab</strong>.
        </p>
      </div>
    </div>
  );
};

export default UsersPage;
